import type { ParcoursResponse } from "../api/parcoursApi";

const MONTHS_FR = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];

function fmtLong(iso?: string | null): string {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  return `${d} ${MONTHS_FR[m - 1]} ${y}`;
}

function fmtShort(iso?: string | null): string {
  if (!iso) return "";
  const [, m, d] = iso.split("-").map(Number);
  if (!m || !d) return iso ?? "";
  return `${d} ${MONTHS_FR[m - 1]}`;
}

/** Coupe au dernier mot entier avant `max` et ajoute « … » si le texte est tronqué. */
function summarize(text: string, max = 220): string {
  const t = text.trim();
  if (t.length <= max) return t;
  const cut = t.slice(0, max + 1);
  const atSpace = cut.lastIndexOf(" ");
  const kept = atSpace > max * 0.6 ? cut.slice(0, atSpace) : t.slice(0, max);
  return kept.replace(/[\s.,;:!?…]+$/, "") + "…";
}

/**
 * Libellé lisible d'une période, sans répéter le mois ni l'année :
 *   1 jour              → « 4 octobre 2026 »
 *   même mois           → « 12 → 13 septembre 2026 »
 *   même année          → « 28 septembre → 4 octobre 2026 »
 *   années différentes  → « 28 décembre 2026 → 3 janvier 2027 »
 */
function fmtRange(start?: string | null, end?: string | null): string {
  if (!start) return "";
  if (!end || end === start) return fmtLong(start);
  const [startYear, startMonth] = start.split("-");
  const [endYear, endMonth] = end.split("-");
  if (startYear !== endYear) return `${fmtLong(start)} → ${fmtLong(end)}`;
  if (startMonth !== endMonth) return `${fmtShort(start)} → ${fmtLong(end)}`;
  return `${Number(start.slice(8, 10))} → ${fmtLong(end)}`;
}

function deriveStatus(dates: string[]): "live" | "soon" | "past" {
  if (dates.length === 0) return "soon";
  const today = new Date().toISOString().slice(0, 10);
  const first = dates[0];
  const last = dates[dates.length - 1];
  if (today < first) return "soon";
  if (today > last) return "past";
  return "live";
}

/** Map a backend ParcoursResponse to the legacy `window.PA_DATA.parcours` shape. */
export function toLegacyParcours(p: ParcoursResponse) {
  // Start: earliest slot.date. End: latest of slot.endDate ?? slot.date.
  const startCandidates = (p.dates ?? []).map((d) => d.date).filter(Boolean).sort();
  const endCandidates = (p.dates ?? [])
    .map((d) => d.endDate || d.date)
    .filter(Boolean)
    .sort();
  const dateStrings = startCandidates;
  const start = startCandidates[0];
  const end = endCandidates[endCandidates.length - 1];

  const datesDisplay = fmtRange(start, end);

  return {
    id: p.id,
    slug: p.id,
    title: p.title,
    image: p.imageLink || "/assets/hero-landing.png",
    /** false quand `image` n'est que l'illustration de repli. */
    hasImage: Boolean(p.imageLink),
    edition: p.region ?? "",
    dates: datesDisplay,
    dateStart: fmtShort(start),
    dateEnd: fmtShort(end),
    startISO: start ?? "",
    endISO: end ?? "",
    status: deriveStatus(dateStrings),
    region: p.region ?? p.mainCommune ?? "",
    communes: p.mainCommune ? [p.mainCommune] : [],
    adminCommunes: p.mainCommune ? [p.mainCommune] : [],
    summary: summarize(p.description ?? ""),
    description: p.description ?? "",
    longDescription: p.longDescription ?? "",
    contentMode: p.contentMode ?? "PROGRAMME",
    theme: "",
    horaires: "",
    artistIds: (p.artists ?? []).map((a) => a.id),
    artistNames: (p.artists ?? []).map((a) => a.name),
    venueIds: (p.lieux ?? []).map((l) => l.id),
    artistCount: p.artists?.length ?? 0,
    venueCount: p.lieux?.length ?? 0,
    days: dateStrings.length,
    paid: Boolean(p.paid),
  };
}
